import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { leaveRequestService } from '../services/leaveRequestService';
import { leaveTypeService } from '../services/leaveTypeService';
import { leaveBalanceService } from '../services/leaveBalanceService';
import { getUsers } from '../services/userService';
import api from '../services/api';
import type { LeaveRequest, CreateLeaveRequestData } from '../types/leaveRequest';
import type { LeaveType } from '../types/leaveType';
import type { LeaveBalance } from '../types/leaveBalance';
import type { User } from '../types/user';
import Pagination from '../components/Pagination';
import EmploymentFilter, { type Employment } from '../components/EmploymentFilter';

interface UserPermissions {
  canApproveCorrections: boolean;
  canApproveOwnRequests: boolean;
  attendanceViewScope: 'self' | 'department' | 'company' | 'custom';
  viewableUserIds?: number[];
}

const LeaveRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [employment, setEmployment] = useState<Employment>('active');
  // 初回ロードと在職状態の effect が同時に走ると同じ API を2回叩くため、初回だけ後者を止める。
  const skipFirstEmployment = useRef(true);
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [allRequests, setAllRequests] = useState<LeaveRequest[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [userBalances, setUserBalances] = useState<LeaveBalance[]>([]);

  // フィルター状態（実際の値）
  const [filterStatus, setFilterStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [selectedUserId, setSelectedUserId] = useState<number | 'all'>('all');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // フィルター状態（一時値）
  const [tempFilterStatus, setTempFilterStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [tempSelectedUserId, setTempSelectedUserId] = useState<number | 'all'>('all');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('all');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [tempFilterMonth, setTempFilterMonth] = useState('');
  const [tempFilterDateFrom, setTempFilterDateFrom] = useState('');
  const [tempFilterDateTo, setTempFilterDateTo] = useState('');

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // ページネーション状態
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // 新規申請フォームデータ
  const [formData, setFormData] = useState<CreateLeaveRequestData>({
    leave_type_id: 0,
    start_date: '',
    end_date: '',
    days: 0,
    hours: 0,
    reason: '',
  });

  // 開始日と終了日の日数差分を計算（両端を含む）
  const calculateDaysDiff = (startDate: string, endDate: string): number => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (end < start) return 0;
    return Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  };

  // start_date または end_date が変更されたときに日数を自動計算
  useEffect(() => {
    if (formData.start_date && formData.end_date) {
      const daysDiff = calculateDaysDiff(formData.start_date, formData.end_date);
      if (daysDiff >= 2) {
        // 2日以上の連続休暇の場合、日数を自動設定、時間は0にリセット
        setFormData(prev => ({
          ...prev,
          days: daysDiff,
          hours: 0
        }));
      }
    }
  }, [formData.start_date, formData.end_date]);

  // 権限チェック
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const permsResponse = await api.get<UserPermissions>('/auth/permissions');
        setPermissions(permsResponse.data);
      } catch (err) {
        console.error('権限取得エラー:', err);
      }
    };

    if (user) {
      checkPermissions();
    }
  }, [user]);

  // 初回ロード：ユーザー一覧と休暇申請を取得し、デフォルト表示（全従業員、承認待ち、全期間）
  useEffect(() => {
    if (user) {
      loadInitialData();
    }
  }, [user]);

  // 在職状態を変えたら**対象者の候補だけ**取り直す。
  //
  // ここで一覧ごと読み直すと、利用者が選んだ状態（承認済み等）や期間はそのまま
  // 画面に残るのに、中身だけが既定（承認待ち・全期間）へ差し替わる。
  // 「承認済み・8月」と表示されたまま9月の承認待ちが出る、という食い違いになる。
  useEffect(() => {
    // 初回は上の初回ロードが同じ内容を取るので、ここでは走らせない（二重取得の防止）。
    if (skipFirstEmployment.current) {
      skipFirstEmployment.current = false;
      return;
    }
    if (!user) return;

    let cancelled = false;
    (async () => {
      try {
        const usersData = await getUsers(employment);
        if (cancelled) return;
        setUsers(usersData);

        // 選択中の対象者が候補から消えたら「全従業員」へ戻す。
        // select は value に一致する option が無いと**空欄ではなく先頭（＝全従業員）**を
        // 描くため、状態を戻さないと「全従業員と表示されているのに1人分しか出ない」
        // という、画面から読み取れない絞り込みが残る。
        const stillListed = (id: number | 'all') => id === 'all' || usersData.some(u => u.id === id);
        setTempSelectedUserId(prev => (stillListed(prev) ? prev : 'all'));
        setSelectedUserId(prev => (stillListed(prev) ? prev : 'all'));
      } catch (err: any) {
        if (!cancelled) setError(err.response?.data?.error || '従業員一覧の取得に失敗しました');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employment, user]);

  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      setError('');

      // ユーザー一覧、休暇申請、休暇種別、残数を並行取得
      const [usersData, requestsData, leaveTypesData, balancesData] = await Promise.all([
        getUsers(employment),
        leaveRequestService.getLeaveRequests(),
        leaveTypeService.getAll(),
        user ? leaveBalanceService.getUserBalances(user.id) : Promise.resolve([])
      ]);

      setUsers(usersData);
      setAllRequests(requestsData);
      setLeaveTypes(leaveTypesData);
      setUserBalances(balancesData);

      // デフォルト表示：全従業員、承認待ち、全期間
      const filtered = requestsData.filter((req: LeaveRequest) => req.status === 'pending');
      setRequests(filtered);
    } catch (err: any) {
      setError(err.response?.data?.error || 'データの取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // 表示ボタン押下時のフィルタリング
  const loadRequests = async () => {
    try {
      setIsLoading(true);
      setError('');

      // 実際の値を更新
      setFilterStatus(tempFilterStatus);
      setSelectedUserId(tempSelectedUserId);
      setFilterMonth(tempFilterMonth);
      setFilterDateFrom(tempFilterDateFrom);
      setFilterDateTo(tempFilterDateTo);

      // サーバーから最新データを取得
      const requestsData = await leaveRequestService.getLeaveRequests();
      setAllRequests(requestsData);

      let filtered = [...requestsData];

      // 状態フィルター
      filtered = filtered.filter((req: LeaveRequest) => req.status === tempFilterStatus);

      // 従業員フィルター
      if (tempSelectedUserId !== 'all') {
        filtered = filtered.filter((req: LeaveRequest) => req.userId === tempSelectedUserId);
      }

      // 期間フィルター（承認待ち以外の場合のみ）
      if (tempFilterStatus !== 'pending') {
        if (tempFilterMonth) {
          const [year, month] = tempFilterMonth.split('-');
          const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
          const endDate = new Date(parseInt(year), parseInt(month), 0);
          filtered = filtered.filter((req: LeaveRequest) => {
            const reqDate = new Date(req.startDate);
            return reqDate >= startDate && reqDate <= endDate;
          });
        } else if (tempFilterDateFrom && tempFilterDateTo) {
          const startDate = new Date(tempFilterDateFrom);
          const endDate = new Date(tempFilterDateTo);
          filtered = filtered.filter((req: LeaveRequest) => {
            const reqDate = new Date(req.startDate);
            return reqDate >= startDate && reqDate <= endDate;
          });
        }
      }

      setRequests(filtered);
      setCurrentPage(1); // フィルタリング時は1ページ目に戻る
    } catch (err: any) {
      setError(err.response?.data?.error || '休暇申請の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    try {
      await leaveRequestService.createLeaveRequest(formData);
      setSuccessMessage('休暇申請を作成しました');
      setShowCreateForm(false);
      setFormData({
        leave_type_id: 0,
        start_date: '',
        end_date: '',
        days: 0,
        hours: 0,
        reason: '',
      });
      await loadRequests();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || '休暇申請の作成に失敗しました');
    }
  };

  const handleApprove = async (id: number) => {
    if (!window.confirm('この休暇申請を承認しますか？')) {
      return;
    }

    try {
      setError('');
      setSuccessMessage('');
      await leaveRequestService.approveLeaveRequest(id);
      setSuccessMessage('休暇申請を承認しました');
      await loadRequests();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || '休暇申請の承認に失敗しました');
    }
  };

  const handleReject = async (id: number) => {
    if (!window.confirm('この休暇申請を却下しますか？')) {
      return;
    }

    try {
      setError('');
      setSuccessMessage('');
      await leaveRequestService.rejectLeaveRequest(id);
      setSuccessMessage('休暇申請を却下しました');
      await loadRequests();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || '休暇申請の却下に失敗しました');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('この休暇申請を取り下げますか？')) {
      return;
    }

    try {
      setError('');
      setSuccessMessage('');
      await leaveRequestService.deleteLeaveRequest(id);
      setSuccessMessage('休暇申請を取り下げました');
      await loadRequests();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || '休暇申請の取り下げに失敗しました');
    }
  };

  const handleCancel = async (id: number) => {
    if (!window.confirm('この休暇申請を取消しますか？\n※ 取消すると休暇残数が復元されます。')) {
      return;
    }

    try {
      setError('');
      setSuccessMessage('');
      await leaveRequestService.cancelLeaveRequest(id);
      setSuccessMessage('休暇申請を取消しました');
      await loadRequests();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || '休暇申請の取消に失敗しました');
    }
  };

  // 選択した休暇種別の残数を取得
  const getBalanceForLeaveType = (leaveTypeId: number): { days: number; hours: number; totalHours: number } | null => {
    if (!leaveTypeId) return null;

    const balance = userBalances.find(b => b.leave_type_id === leaveTypeId);
    if (!balance) return null;

    // 企業設定から所定労働時間を取得（デフォルト8時間）
    const standardWorkHours = 8; // TODO: 企業設定から取得

    const totalHours = balance.balance_hours || 0;
    const days = Math.floor(totalHours / standardWorkHours);
    const hours = totalHours % standardWorkHours;

    return { days, hours, totalHours };
  };

  // 部署でフィルタリングされたユーザー一覧
  const filteredUsers = users.filter(u => {
    if (selectedDepartmentId !== 'all' && u.department_id?.toString() !== selectedDepartmentId) {
      return false;
    }
    if (userSearchQuery && !u.name.toLowerCase().includes(userSearchQuery.toLowerCase()) &&
        !u.username.toLowerCase().includes(userSearchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });

  // 部署一覧を取得
  const departments = Array.from(new Set(users.map(u => u.department_id).filter(Boolean)))
    .map(deptId => {
      const user = users.find(u => u.department_id === deptId);
      return { id: deptId, name: user?.department_name || '不明' };
    });

  // 終了日の最大値を計算（開始日から30日以内）
  const getMaxDateTo = () => {
    if (!tempFilterDateFrom) return '';
    const fromDate = new Date(tempFilterDateFrom);
    fromDate.setDate(fromDate.getDate() + 30);
    return fromDate.toISOString().split('T')[0];
  };

  // クリアボタンの処理
  const handleClear = () => {
    setTempFilterStatus('pending');
    setFilterStatus('pending');
    setTempSelectedUserId('all');
    setSelectedUserId('all');
    setSelectedDepartmentId('all');
    setUserSearchQuery('');
    setTempFilterMonth('');
    setFilterMonth('');
    setTempFilterDateFrom('');
    setFilterDateFrom('');
    setTempFilterDateTo('');
    setFilterDateTo('');

    // デフォルト表示に戻す（全従業員、承認待ち、全期間）
    const filtered = allRequests.filter((req: LeaveRequest) => req.status === 'pending');
    setRequests(filtered);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      weekday: 'short'
    });
  };

  const formatDateTime = (dateTimeString: string | null) => {
    if (!dateTimeString) return '---';
    const date = new Date(dateTimeString);
    return date.toLocaleString('ja-JP', {
      timeZone: 'Asia/Tokyo',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: { backgroundColor: '#fff3cd', color: '#856404', text: '承認待ち' },
      approved: { backgroundColor: '#d4edda', color: '#155724', text: '承認済み' },
      rejected: { backgroundColor: '#f8d7da', color: '#721c24', text: '却下' }
    };

    const style = styles[status as keyof typeof styles] || styles.pending;

    return (
      <span style={{
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold',
        ...style
      }}>
        {style.text}
      </span>
    );
  };

  // 時間数を日数+時間で表示（1日=8時間として計算）
  const formatHours = (hours: number): string => {
    const days = Math.floor(hours / 8);
    const remainingHours = hours % 8;
    if (days > 0 && remainingHours > 0) {
      return `${days}日${remainingHours}時間`;
    } else if (days > 0) {
      return `${days}日`;
    } else {
      return `${remainingHours}時間`;
    }
  };

  if (isLoading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>読み込み中...</div>;
  };

  // 表示ボタンの有効/無効判定
  const isPeriodRequired = tempFilterStatus !== 'pending';
  const isPeriodSelected = tempFilterMonth || (tempFilterDateFrom && tempFilterDateTo);
  const isDisplayButtonEnabled = !isPeriodRequired || isPeriodSelected;

  // ページネーション用のデータ計算
  const totalPages = Math.ceil(requests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRequests = requests.slice(startIndex, endIndex);

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: 'clamp(10px, 3vw, 20px)' }}>
      <header style={{ marginBottom: 'clamp(15px, 4vw, 30px)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'clamp(10px, 3vw, 20px)', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
            <h1 style={{ fontSize: 'clamp(18px, 5vw, 24px)', margin: 0 }}>休暇申請一覧</h1>
            {user && (
              <p style={{ margin: '5px 0 0 0', color: '#6c757d', fontSize: 'clamp(12px, 3vw, 14px)' }}>
                {user.name}さん ({user.companyCode})
              </p>
            )}
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              padding: 'clamp(6px, 1.5vw, 8px) clamp(12px, 3vw, 16px)',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: 'clamp(12px, 2.5vw, 14px)',
              whiteSpace: 'nowrap'
            }}
          >
            ダッシュボードに戻る
          </button>
        </div>

        {/* フィルターセクション */}
        <div style={{
          padding: 'clamp(12px, 3vw, 20px)',
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '1px solid #dee2e6',
          marginBottom: 'clamp(10px, 3vw, 20px)'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: 'clamp(14px, 3.5vw, 16px)' }}>フィルター</h3>

          {/* 状態選択 */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: 'clamp(12px, 3vw, 14px)' }}>状態</label>
            <div style={{ display: 'flex', gap: 'clamp(5px, 1.5vw, 8px)', flexWrap: 'wrap' }}>
              {['pending', 'approved', 'rejected'].map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    setTempFilterStatus(s as any);
                    // 承認待ちに変更した場合、期間フィルターをクリア
                    if (s === 'pending') {
                      setTempFilterMonth('');
                      setTempFilterDateFrom('');
                      setTempFilterDateTo('');
                    }
                  }}
                  style={{
                    padding: 'clamp(6px, 1.5vw, 8px) clamp(12px, 3vw, 16px)',
                    backgroundColor: tempFilterStatus === s ? '#007bff' : '#e9ecef',
                    color: tempFilterStatus === s ? 'white' : '#212529',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {s === 'pending' ? '承認待ち' : s === 'approved' ? '承認済み' : '却下済み'}
                </button>
              ))}
            </div>
          </div>

          {/* 従業員選択 */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: 'clamp(12px, 3vw, 14px)' }}>従業員</label>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              {/* 部署選択 */}
              <div style={{ flex: '1 1 150px', minWidth: '150px' }}>
                <label htmlFor="department-filter" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#6c757d' }}>
                  部署
                </label>
                <select
                  id="department-filter"
                  value={selectedDepartmentId}
                  onChange={(e) => setSelectedDepartmentId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: 'clamp(12px, 2.5vw, 14px)'
                  }}
                >
                  <option value="all">全部署</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id?.toString() || ''}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* 在職状態 */}
          <div style={{ flex: '1 1 150px', minWidth: '150px' }}>
            <EmploymentFilter id="leave-requests-employment" value={employment} onChange={setEmployment} count={users.length} layout="stacked" />
          </div>

          {/* 検索 */}
              <div style={{ flex: '1 1 150px', minWidth: '150px' }}>
                <label htmlFor="user-search" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#6c757d' }}>
                  検索（氏名・ログインID）
                </label>
                <input
                  id="user-search"
                  type="text"
                  placeholder="検索..."
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: 'clamp(12px, 2.5vw, 14px)'
                  }}
                />
              </div>

              {/* 従業員選択 */}
              <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                <label htmlFor="user-filter" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#6c757d' }}>
                  選択
                </label>
                <select
                  id="user-filter"
                  value={tempSelectedUserId}
                  onChange={(e) => setTempSelectedUserId(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                  style={{
                    width: '100%',
                    padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: 'clamp(12px, 2.5vw, 14px)'
                  }}
                >
                  <option value="all">全従業員</option>
                  {filteredUsers.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.username})
                      {u.department_name && ` - ${u.department_name}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* 期間選択（承認待ち以外の場合のみ） */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: 'clamp(12px, 3vw, 14px)' }}>
              期間
              {tempFilterStatus === 'pending' && (
                <span style={{ marginLeft: '8px', fontSize: 'clamp(11px, 2.5vw, 12px)', fontWeight: 'normal', color: '#6c757d' }}>
                  （承認待ち時は全期間を表示します）
                </span>
              )}
            </label>
            <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              {/* 月選択 */}
              <div style={{ flex: '1 1 130px', minWidth: '130px' }}>
                <label htmlFor="month-filter" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#6c757d' }}>
                  月選択
                </label>
                <input
                  id="month-filter"
                  type="month"
                  value={tempFilterMonth}
                  onChange={(e) => {
                    setTempFilterMonth(e.target.value);
                    if (e.target.value) {
                      setTempFilterDateFrom('');
                      setTempFilterDateTo('');
                    }
                  }}
                  disabled={tempFilterStatus === 'pending' || !!tempFilterDateFrom || !!tempFilterDateTo}
                  style={{
                    width: '100%',
                    padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    backgroundColor: (tempFilterStatus === 'pending' || tempFilterDateFrom || tempFilterDateTo) ? '#e9ecef' : 'white',
                    cursor: (tempFilterStatus === 'pending' || tempFilterDateFrom || tempFilterDateTo) ? 'not-allowed' : 'pointer'
                  }}
                />
                <div style={{ minHeight: '18px', marginTop: '2px' }}></div>
              </div>

              {/* 開始日 */}
              <div style={{ flex: '1 1 130px', minWidth: '130px' }}>
                <label htmlFor="date-from" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#6c757d' }}>
                  開始日
                </label>
                <input
                  id="date-from"
                  type="date"
                  value={tempFilterDateFrom}
                  onChange={(e) => {
                    setTempFilterDateFrom(e.target.value);
                    if (e.target.value) {
                      setTempFilterMonth('');
                    }
                  }}
                  disabled={tempFilterStatus === 'pending' || !!tempFilterMonth}
                  style={{
                    width: '100%',
                    padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    backgroundColor: (tempFilterStatus === 'pending' || tempFilterMonth) ? '#e9ecef' : 'white',
                    cursor: (tempFilterStatus === 'pending' || tempFilterMonth) ? 'not-allowed' : 'pointer'
                  }}
                />
                <div style={{ minHeight: '18px', marginTop: '2px' }}></div>
              </div>

              {/* 終了日 */}
              <div style={{ flex: '1 1 130px', minWidth: '130px' }}>
                <label htmlFor="date-to" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#6c757d' }}>
                  終了日
                </label>
                <input
                  id="date-to"
                  type="date"
                  value={tempFilterDateTo}
                  onChange={(e) => {
                    setTempFilterDateTo(e.target.value);
                    if (e.target.value) {
                      setTempFilterMonth('');
                    }
                  }}
                  disabled={tempFilterStatus === 'pending' || !!tempFilterMonth}
                  min={tempFilterDateFrom || undefined}
                  max={getMaxDateTo() || undefined}
                  style={{
                    width: '100%',
                    padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    backgroundColor: (tempFilterStatus === 'pending' || tempFilterMonth) ? '#e9ecef' : 'white',
                    cursor: (tempFilterStatus === 'pending' || tempFilterMonth) ? 'not-allowed' : 'pointer'
                  }}
                />
                <div style={{ minHeight: '18px', marginTop: '2px' }}>
                  {tempFilterDateFrom && tempFilterStatus !== 'pending' && !tempFilterMonth && (
                    <small style={{ color: '#6c757d', fontSize: 'clamp(10px, 2vw, 12px)' }}>
                      ※ 開始日から30日以内まで選択可能
                    </small>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ボタン */}
          <div style={{ display: 'flex', gap: '10px', marginTop: '15px', flexWrap: 'wrap' }}>
            <button
              onClick={loadRequests}
              disabled={!isDisplayButtonEnabled}
              style={{
                padding: 'clamp(8px, 2vw, 10px) clamp(18px, 4vw, 24px)',
                backgroundColor: !isDisplayButtonEnabled ? '#6c757d' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: !isDisplayButtonEnabled ? 'not-allowed' : 'pointer',
                fontSize: 'clamp(12px, 2.5vw, 14px)',
                fontWeight: 'bold',
                opacity: !isDisplayButtonEnabled ? 0.6 : 1,
                whiteSpace: 'nowrap'
              }}
            >
              表示
            </button>
            <button
              onClick={handleClear}
              style={{
                padding: 'clamp(8px, 2vw, 10px) clamp(18px, 4vw, 24px)',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: 'clamp(12px, 2.5vw, 14px)',
                whiteSpace: 'nowrap'
              }}
            >
              クリア
            </button>
            <button
                onClick={() => setShowCreateForm(!showCreateForm)}
                style={{
                  padding: 'clamp(8px, 2vw, 10px) clamp(18px, 4vw, 24px)',
                  backgroundColor: showCreateForm ? '#6c757d' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: 'clamp(12px, 2.5vw, 14px)',
                  whiteSpace: 'nowrap'
                }}
              >
                {showCreateForm ? 'キャンセル' : '+ 新規申請'}
              </button>
          </div>
        </div>

        {/* 新規申請フォーム */}
        {showCreateForm && (
          <div style={{
            padding: 'clamp(12px, 3vw, 20px)',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #dee2e6',
            marginBottom: 'clamp(10px, 3vw, 20px)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: 'clamp(14px, 3.5vw, 16px)' }}>新規休暇申請</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
                <div>
                  <label htmlFor="leave_type_id" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', fontWeight: 'bold' }}>
                    休暇種類 *
                  </label>
                  <select
                    id="leave_type_id"
                    value={formData.leave_type_id}
                    onChange={(e) => setFormData({ ...formData, leave_type_id: parseInt(e.target.value) })}
                    required
                    style={{
                      width: '100%',
                      padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: 'clamp(12px, 2.5vw, 14px)'
                    }}
                  >
                    <option value="0">選択してください</option>
                    {leaveTypes.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  {formData.leave_type_id > 0 && (() => {
                    const balance = getBalanceForLeaveType(formData.leave_type_id);
                    if (balance) {
                      return (
                        <small style={{ color: '#6c757d', fontSize: 'clamp(10px, 2vw, 12px)', marginTop: '2px', display: 'block' }}>
                          残数: {balance.days}日{balance.hours > 0 && `${balance.hours}時間`}
                        </small>
                      );
                    }
                    return null;
                  })()}
                </div>

                <div>
                  <label htmlFor="start_date" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', fontWeight: 'bold' }}>
                    開始日 *
                  </label>
                  <input
                    type="date"
                    id="start_date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: 'clamp(12px, 2.5vw, 14px)'
                    }}
                  />
                </div>

                <div>
                  <label htmlFor="end_date" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', fontWeight: 'bold' }}>
                    終了日 *
                  </label>
                  <input
                    type="date"
                    id="end_date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: 'clamp(12px, 2.5vw, 14px)'
                    }}
                  />
                </div>

                <div>
                  <label htmlFor="days" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', fontWeight: 'bold' }}>
                    日数
                  </label>
                  <input
                    type="number"
                    id="days"
                    value={formData.days}
                    onChange={(e) => {
                      // 対象期間を超える日数を入力できないようにする。
                      // 1日だけの申請で3日などを入れると、実際は1日しか休まないのに
                      // 残数を3日分消費できてしまうため。
                      const limit = calculateDaysDiff(formData.start_date, formData.end_date) || 1;
                      const entered = parseFloat(e.target.value) || 0;
                      setFormData({ ...formData, days: Math.min(Math.max(entered, 0), limit) });
                    }}
                    min="0"
                    max={calculateDaysDiff(formData.start_date, formData.end_date) || 1}
                    step="0.5"
                    disabled={(() => {
                      const daysDiff = calculateDaysDiff(formData.start_date, formData.end_date);
                      // 2日以上の場合：自動計算のため無効化
                      if (daysDiff >= 2) return true;
                      // 1日のみの場合：時間が入力されていれば無効化
                      if (daysDiff === 1 && formData.hours > 0) return true;
                      return false;
                    })()}
                    style={{
                      width: '100%',
                      padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: 'clamp(12px, 2.5vw, 14px)',
                      backgroundColor: (() => {
                        const daysDiff = calculateDaysDiff(formData.start_date, formData.end_date);
                        return (daysDiff >= 2 || (daysDiff === 1 && formData.hours > 0)) ? '#f0f0f0' : 'white';
                      })()
                    }}
                  />
                  {(() => {
                    const daysDiff = calculateDaysDiff(formData.start_date, formData.end_date);
                    if (daysDiff >= 2) {
                      return (
                        <small style={{ color: '#28a745', fontSize: 'clamp(10px, 2vw, 12px)', marginTop: '2px', display: 'block' }}>
                          ※ 2日以上の連続休暇のため、日数は自動計算されます
                        </small>
                      );
                    }
                    return null;
                  })()}
                </div>

                <div>
                  <label htmlFor="hours" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', fontWeight: 'bold' }}>
                    時間
                  </label>
                  <input
                    type="number"
                    id="hours"
                    value={formData.hours}
                    onChange={(e) => setFormData({ ...formData, hours: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.5"
                    disabled={(() => {
                      const daysDiff = calculateDaysDiff(formData.start_date, formData.end_date);
                      // 2日以上の場合：無効化
                      if (daysDiff >= 2) return true;
                      // 1日のみの場合：日数が入力されていれば無効化
                      if (daysDiff === 1 && formData.days > 0) return true;
                      return false;
                    })()}
                    style={{
                      width: '100%',
                      padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: 'clamp(12px, 2.5vw, 14px)',
                      backgroundColor: (() => {
                        const daysDiff = calculateDaysDiff(formData.start_date, formData.end_date);
                        return (daysDiff >= 2 || (daysDiff === 1 && formData.days > 0)) ? '#f0f0f0' : 'white';
                      })()
                    }}
                  />
                  <small style={{ color: '#6c757d', fontSize: 'clamp(10px, 2vw, 12px)', marginTop: '2px', display: 'block' }}>
                    ※ 1日のみの場合、日数または時間のいずれかを入力
                  </small>
                </div>
              </div>

              <div style={{ marginTop: '15px' }}>
                <label htmlFor="reason" style={{ display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', fontWeight: 'bold' }}>
                  理由
                </label>
                <textarea
                  id="reason"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    resize: 'vertical'
                  }}
                />
              </div>

              <button
                type="submit"
                style={{
                  marginTop: '15px',
                  padding: 'clamp(8px, 2vw, 10px) clamp(18px, 4vw, 24px)',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: 'clamp(12px, 2.5vw, 14px)',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap'
                }}
              >
                申請
              </button>
            </form>
          </div>
        )}
      </header>

      {error && (
        <div style={{
          padding: '10px',
          marginBottom: '20px',
          borderRadius: '4px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb'
        }}>
          {error}
        </div>
      )}

      {successMessage && (
        <div style={{
          padding: '10px',
          marginBottom: '20px',
          borderRadius: '4px',
          backgroundColor: '#d4edda',
          color: '#155724',
          border: '1px solid #c3e6cb'
        }}>
          {successMessage}
        </div>
      )}

      {requests.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
          {filterStatus === 'pending' ? '承認待ち' : filterStatus === 'approved' ? '承認済み' : '却下された'}申請がありません
        </div>
      ) : (
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '600px' }}>
          <table style={{
            width: '100%',
            minWidth: '900px',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>申請日</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>申請者</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>休暇種類</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>期間</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>取得</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>理由</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>ステータス</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRequests.map((request) => (
                <tr key={request.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                    {formatDateTime(request.createdAt)}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                    {request.userName}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                    {request.leaveTypeName}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(11px, 2.5vw, 13px)' }}>
                    {formatDate(request.startDate)}〜{formatDate(request.endDate)}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                    {formatHours(request.hours)}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(11px, 2.5vw, 13px)', maxWidth: '200px' }}>
                    <div style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }} title={request.reason || ''}>
                      {request.reason || '-'}
                    </div>
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)' }}>
                    {getStatusBadge(request.status)}
                    {request.approverName && (
                      <div style={{ fontSize: 'clamp(9px, 2vw, 11px)', color: '#6c757d', marginTop: '4px' }}>
                        承認者: {request.approverName}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)' }}>
                    {request.status === 'pending' ? (
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {/* 自分の申請の場合：自己承認権限があれば承認・却下ボタンも表示、なければ削除のみ */}
                        {request.userId === user?.id ? (
                          <>
                            {permissions?.canApproveOwnRequests && (
                              <>
                                <button
                                  onClick={() => handleApprove(request.id)}
                                  style={{
                                    padding: 'clamp(4px, 1.5vw, 6px) clamp(8px, 2vw, 12px)',
                                    backgroundColor: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: 'clamp(10px, 2vw, 12px)',
                                    whiteSpace: 'nowrap'
                                  }}
                                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
                                >
                                  承認
                                </button>
                                <button
                                  onClick={() => handleReject(request.id)}
                                  style={{
                                    padding: 'clamp(4px, 1.5vw, 6px) clamp(8px, 2vw, 12px)',
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: 'clamp(10px, 2vw, 12px)',
                                    whiteSpace: 'nowrap'
                                  }}
                                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
                                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
                                >
                                  却下
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleDelete(request.id)}
                              style={{
                                padding: 'clamp(4px, 1.5vw, 6px) clamp(8px, 2vw, 12px)',
                                backgroundColor: '#ffc107',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: 'clamp(10px, 2vw, 12px)',
                                whiteSpace: 'nowrap'
                              }}
                              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e0a800'}
                              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ffc107'}
                            >
                              取下げ
                            </button>
                          </>
                        ) : permissions?.canApproveCorrections ? (
                          <>
                            <button
                              onClick={() => handleApprove(request.id)}
                              style={{
                                padding: 'clamp(4px, 1.5vw, 6px) clamp(8px, 2vw, 12px)',
                                backgroundColor: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: 'clamp(10px, 2vw, 12px)',
                                whiteSpace: 'nowrap'
                              }}
                              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#218838'}
                              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#28a745'}
                            >
                              承認
                            </button>
                            <button
                              onClick={() => handleReject(request.id)}
                              style={{
                                padding: 'clamp(4px, 1.5vw, 6px) clamp(8px, 2vw, 12px)',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: 'clamp(10px, 2vw, 12px)',
                                whiteSpace: 'nowrap'
                              }}
                              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
                              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
                            >
                              却下
                            </button>
                          </>
                        ) : (
                          <span style={{ fontSize: 'clamp(10px, 2vw, 12px)', color: '#6c757d' }}>権限なし</span>
                        )}
                      </div>
                    ) : request.status === 'approved' && permissions?.canApproveCorrections ? (
                      <button
                        onClick={() => handleCancel(request.id)}
                        style={{
                          padding: 'clamp(4px, 1.5vw, 6px) clamp(8px, 2vw, 12px)',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: 'clamp(10px, 2vw, 12px)',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c82333'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc3545'}
                      >
                        取消
                      </button>
                    ) : (
                      <span style={{ fontSize: 'clamp(10px, 2vw, 12px)', color: '#6c757d' }}>-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ページネーション */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        totalItems={requests.length}
      />
    </div>
  );
};

export default LeaveRequestsPage;
